import { Menu, X, ArrowUpRight, Home, Layers, Briefcase, Users, Mail, ExternalLink, ChevronDown, FileText, MapPin, Database } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../lib/ThemeContext';

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      className="relative w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 group overflow-hidden"
      style={{
        background: isDark
          ? 'linear-gradient(135deg, rgba(30,41,59,0.8), rgba(15,23,42,0.9))'
          : 'linear-gradient(135deg, rgba(248,250,252,0.9), rgba(241,245,249,0.8))',
        border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
        boxShadow: isDark
          ? '0 2px 12px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)'
          : '0 2px 8px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
      }}
      aria-label="Toggle theme"
    >
      <div
        className="relative w-5 h-5 transition-all duration-500"
        style={{ transform: isDark ? 'rotate(0deg) scale(1)' : 'rotate(180deg) scale(0.9)' }}
      >
        {isDark ? (
          <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-amber-300" style={{ filter: 'drop-shadow(0 0 4px rgba(252,211,77,0.6))' }}>
            <circle cx="12" cy="12" r="4" fill="currentColor" className="sun-core" />
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
              <line
                key={i}
                x1="12"
                y1="12"
                x2={12 + 7 * Math.cos((angle * Math.PI) / 180)}
                y2={12 + 7 * Math.sin((angle * Math.PI) / 180)}
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                style={{
                  transformOrigin: '12px 12px',
                  opacity: 0.9,
                }}
              />
            ))}
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-slate-600" style={{ filter: 'drop-shadow(0 0 3px rgba(71,85,105,0.3))' }}>
            <path
              d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
              fill="currentColor"
              style={{ opacity: 0.85 }}
            />
          </svg>
        )}
      </div>
      <span
        className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: isDark
            ? 'radial-gradient(circle at center, rgba(252,211,77,0.15) 0%, transparent 70%)'
            : 'radial-gradient(circle at center, rgba(71,85,105,0.1) 0%, transparent 70%)',
        }}
      />
    </button>
  );
}

function NavLink({ to, label, isActive }: { to: string; label: string; isActive: boolean }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to={to}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative px-4 py-2 text-sm font-medium transition-colors duration-200 group"
      style={{
        color: isActive
          ? 'var(--nav-active-color)'
          : hovered
            ? 'var(--nav-hover-color)'
            : 'var(--nav-color)',
      }}
    >
      {label}
      <span
        className="absolute bottom-0 left-4 right-4 h-[1.5px] rounded-full origin-left"
        style={{
          background: 'linear-gradient(90deg, #10b981, #14b8a6)',
          transform: isActive ? 'scaleX(1)' : hovered ? 'scaleX(1)' : 'scaleX(0)',
          transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          boxShadow: isActive || hovered ? '0 0 8px rgba(16,185,129,0.6)' : 'none',
        }}
      />
    </Link>
  );
}

function AmbientNodes() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let raf: number;
    let cleanup: (() => void) | null = null;

    // Defer until after first paint — avoids blocking LCP
    const t = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const W = canvas.offsetWidth;
      const H = canvas.offsetHeight;
      canvas.width = W;
      canvas.height = H;

      const nodes = Array.from({ length: 18 }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
      }));

      let isDarkMode = document.documentElement.classList.contains('dark');

      const draw = () => {
        isDarkMode = document.documentElement.classList.contains('dark');
        ctx.clearRect(0, 0, W, H);

        const nodeColor = isDarkMode ? 'rgba(16,185,129,0.35)' : 'rgba(16,185,129,0.18)';
        const lineColor = isDarkMode ? 'rgba(20,184,166,0.12)' : 'rgba(20,184,166,0.07)';

        for (let i = 0; i < nodes.length; i++) {
          const n = nodes[i];
          n.x += n.vx;
          n.y += n.vy;
          if (n.x < 0 || n.x > W) n.vx *= -1;
          if (n.y < 0 || n.y > H) n.vy *= -1;

          for (let j = i + 1; j < nodes.length; j++) {
            const m = nodes[j];
            const dx = n.x - m.x;
            const dy = n.y - m.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 120) {
              ctx.beginPath();
              ctx.moveTo(n.x, n.y);
              ctx.lineTo(m.x, m.y);
              ctx.strokeStyle = lineColor;
              ctx.lineWidth = (1 - dist / 120) * 0.8;
              ctx.stroke();
            }
          }

          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
          ctx.fillStyle = nodeColor;
          ctx.fill();
        }

        raf = requestAnimationFrame(draw);
      };

      draw();
      cleanup = () => cancelAnimationFrame(raf);
    }, 500);

    return () => {
      clearTimeout(t);
      cleanup?.();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 1 }}
    />
  );
}

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastScroll, setLastScroll] = useState(0);
  const { theme } = useTheme();
  const location = useLocation();
  const isDark = theme === 'dark';

  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY;
      setScrolled(current > 30);
      setHidden(current > 250 && current > lastScroll + 10);
      setLastScroll(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScroll]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const [workDropdownOpen, setWorkDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setWorkDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { to: '/', label: 'Home', icon: Home },
    { to: '/portfolio', label: 'Portfolio', icon: Briefcase },
    { to: '/services', label: 'Services', icon: Layers },
    { to: '/crm', label: 'CRM Products', icon: Database },
    { to: '/google-my-business-services', label: 'GMB Services', icon: MapPin },
    { to: '/about', label: 'About', icon: Users },
    { to: '/blog', label: 'Blog', icon: FileText },
    { to: '/contact', label: 'Contact', icon: Mail },
  ];

  const workLinks = [
    { to: '/portfolio', label: 'Portfolio', icon: Briefcase, description: 'View our projects' },
    { to: '/services', label: 'Services', icon: Layers, description: 'What we build' },
    { to: '/crm', label: 'CRM Products', icon: Database, description: 'Industry-specific CRMs' },
    { to: '/google-my-business-services', label: 'GMB Services', icon: MapPin, description: 'Local SEO & Google Maps' },
  ];

  const isActive = (path: string) => location.pathname === path;

  const glassBg = scrolled
    ? isDark
      ? 'rgba(9,11,17,0.82)'
      : 'rgba(255,255,255,0.82)'
    : isDark
      ? 'rgba(9,11,17,0.4)'
      : 'rgba(255,255,255,0.5)';

  const borderColor = scrolled
    ? isDark
      ? 'rgba(255,255,255,0.06)'
      : 'rgba(0,0,0,0.06)'
    : isDark
      ? 'rgba(255,255,255,0.04)'
      : 'rgba(0,0,0,0.04)';

  return (
    <>
      <style>{`
        :root {
          --nav-color: ${isDark ? 'rgba(209,213,219,0.85)' : 'rgba(55,65,81,0.9)'};
          --nav-hover-color: ${isDark ? '#ffffff' : '#111827'};
          --nav-active-color: ${isDark ? '#34d399' : '#059669'};
        }
        .theme-transition,
        .theme-transition *,
        .theme-transition *::before,
        .theme-transition *::after {
          transition: background-color 0.4s ease, border-color 0.4s ease, color 0.3s ease, box-shadow 0.4s ease !important;
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .sidebar-link-enter {
          animation: fadeInUp 0.35s cubic-bezier(0.16,1,0.3,1) both;
        }
      `}</style>

      <header
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          transform: hidden ? 'translateY(-100%)' : 'translateY(0)',
          transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), height 0.4s ease',
        }}
      >
        <div
          className="relative"
          style={{
            background: glassBg,
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            borderBottom: `1px solid ${borderColor}`,
            boxShadow: scrolled
              ? isDark
                ? '0 4px 32px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.04)'
                : '0 4px 24px rgba(0,0,0,0.08), 0 1px 0 rgba(255,255,255,0.8)'
              : 'none',
            transition: 'background 0.5s ease, box-shadow 0.5s ease, border-color 0.5s ease',
          }}
        >
          <AmbientNodes />

          {scrolled && (
            <div
              className="absolute top-0 left-0 right-0 h-px"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(16,185,129,0.4) 30%, rgba(20,184,166,0.6) 50%, rgba(16,185,129,0.4) 70%, transparent 100%)',
                opacity: 0.7,
              }}
            />
          )}

          <div className="container-narrow relative z-10">
            <div
              className="flex justify-between items-center transition-all duration-400"
              style={{ height: scrolled ? '60px' : '76px' }}
            >
              <Link to="/" className="flex items-center gap-3 group">
                <div
                  className="relative flex items-center justify-center transition-all duration-400 group-hover:scale-110"
                  style={{
                    width: scrolled ? '36px' : '44px',
                    height: scrolled ? '36px' : '44px',
                    transition: 'width 0.4s ease, height 0.4s ease, transform 0.3s cubic-bezier(0.16,1,0.3,1)',
                  }}
                >
                  <img
                    src="/ChatGPT_Image_Mar_14,_2026,_08_43_44_PM.png"
                    alt="Matchless Digital Hub"
                    className="w-full h-full object-contain rounded-xl"
                    style={{ transition: 'filter 0.3s ease' }}
                  />
                </div>

                <div
                  className="overflow-hidden transition-all duration-400"
                  style={{ maxWidth: scrolled ? '0px' : '160px', opacity: scrolled ? 0 : 1, transition: 'max-width 0.4s ease, opacity 0.3s ease' }}
                >
                  <span className="block text-base font-bold text-gray-900 dark:text-white leading-tight whitespace-nowrap">Matchless Digital</span>
                  <span className="block text-[9px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-[0.25em] leading-tight whitespace-nowrap">Hub</span>
                </div>

                <div
                  className="overflow-hidden transition-all duration-400"
                  style={{ maxWidth: scrolled ? '120px' : '0px', opacity: scrolled ? 1 : 0, transition: 'max-width 0.4s ease, opacity 0.3s ease 0.1s' }}
                >
                  <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">MDH</span>
                </div>
              </Link>

              <nav className="hidden lg:flex items-center gap-0.5">
                <NavLink to="/" label="Home" isActive={isActive('/')} />

                <div ref={dropdownRef} className="relative">
                  <button
                    onClick={() => setWorkDropdownOpen(!workDropdownOpen)}
                    onMouseEnter={() => setWorkDropdownOpen(true)}
                    aria-label="Work — expand menu"
                    aria-expanded={workDropdownOpen}
                    aria-haspopup="true"
                    className="relative px-4 py-2 text-sm font-medium transition-colors duration-200 flex items-center gap-1"
                    style={{
                      color: (isActive('/portfolio') || isActive('/services') || location.pathname.startsWith('/crm') || isActive('/google-my-business-services'))
                        ? 'var(--nav-active-color)'
                        : workDropdownOpen
                          ? 'var(--nav-hover-color)'
                          : 'var(--nav-color)',
                    }}
                  >
                    Work
                    <ChevronDown
                      size={14}
                      className="transition-transform duration-200"
                      style={{ transform: workDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }}
                    />
                    <span
                      className="absolute bottom-0 left-4 right-4 h-[1.5px] rounded-full origin-left"
                      style={{
                        background: 'linear-gradient(90deg, #10b981, #14b8a6)',
                        transform: (isActive('/portfolio') || isActive('/services') || location.pathname.startsWith('/crm') || isActive('/google-my-business-services')) ? 'scaleX(1)' : workDropdownOpen ? 'scaleX(1)' : 'scaleX(0)',
                        transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                        boxShadow: (isActive('/portfolio') || isActive('/services') || location.pathname.startsWith('/crm') || isActive('/google-my-business-services')) || workDropdownOpen ? '0 0 8px rgba(16,185,129,0.6)' : 'none',
                      }}
                    />
                  </button>
                  <div
                    onMouseLeave={() => setWorkDropdownOpen(false)}
                    className="absolute top-full left-1/2 pt-2"
                    style={{
                      zIndex: 100,
                      opacity: workDropdownOpen ? 1 : 0,
                      pointerEvents: workDropdownOpen ? 'auto' : 'none',
                      transform: workDropdownOpen ? 'translateY(0) translateX(-50%)' : 'translateY(-8px) translateX(-50%)',
                      transition: 'opacity 0.2s ease, transform 0.25s cubic-bezier(0.16,1,0.3,1)',
                    }}
                  >
                    <div
                      className="rounded-2xl p-2 min-w-[200px]"
                      style={{
                        background: isDark ? 'rgba(17,24,39,0.95)' : 'rgba(255,255,255,0.98)',
                        border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                        backdropFilter: 'blur(20px)',
                        boxShadow: isDark
                          ? '0 10px 40px -10px rgba(0,0,0,0.5)'
                          : '0 10px 40px -10px rgba(0,0,0,0.15)',
                      }}
                    >
                      {workLinks.map(link => {
                        const Icon = link.icon;
                        return (
                          <Link
                            key={link.to}
                            to={link.to}
                            onClick={() => setWorkDropdownOpen(false)}
                            className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group"
                            style={{
                              background: isActive(link.to)
                                ? isDark ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.08)'
                                : 'transparent',
                            }}
                            onMouseEnter={e => {
                              if (!isActive(link.to)) {
                                (e.currentTarget as HTMLElement).style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
                              }
                            }}
                            onMouseLeave={e => {
                              if (!isActive(link.to)) {
                                (e.currentTarget as HTMLElement).style.background = 'transparent';
                              }
                            }}
                          >
                            <div
                              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-200"
                              style={{
                                background: isActive(link.to)
                                  ? isDark ? 'rgba(16,185,129,0.2)' : 'rgba(16,185,129,0.15)'
                                  : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                              }}
                            >
                              <Icon
                                size={16}
                                style={{
                                  color: isActive(link.to) ? '#10b981' : isDark ? 'rgba(156,163,175,0.9)' : 'rgba(107,114,128,0.9)',
                                }}
                              />
                            </div>
                            <div>
                              <div
                                className="text-sm font-medium"
                                style={{
                                  color: isActive(link.to)
                                    ? isDark ? '#34d399' : '#059669'
                                    : isDark ? 'rgba(229,231,235,0.9)' : 'rgba(17,24,39,0.9)',
                                }}
                              >
                                {link.label}
                              </div>
                              <div
                                className="text-xs"
                                style={{ color: isDark ? 'rgba(156,163,175,0.7)' : 'rgba(107,114,128,0.8)' }}
                              >
                                {link.description}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <NavLink to="/about" label="About" isActive={isActive('/about')} />
                <NavLink to="/blog" label="Blog" isActive={isActive('/blog')} />
                <NavLink to="/contact" label="Contact" isActive={isActive('/contact')} />
              </nav>

              <div className="flex items-center gap-2.5">
                <ThemeToggle />

                <Link
                  to="/contact"
                  className="hidden lg:flex items-center gap-1.5 text-sm font-semibold text-white rounded-xl overflow-hidden relative group"
                  style={{
                    padding: scrolled ? '8px 16px' : '10px 20px',
                    background: 'linear-gradient(135deg, #059669, #0d9488)',
                    boxShadow: '0 2px 12px rgba(16,185,129,0.25)',
                    transition: 'padding 0.4s ease, box-shadow 0.3s ease, transform 0.3s cubic-bezier(0.16,1,0.3,1)',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 0 0 3px rgba(16,185,129,0.2), 0 8px 24px rgba(16,185,129,0.4)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(16,185,129,0.25)';
                  }}
                >
                  <span
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                    style={{ background: 'linear-gradient(135deg, #10b981, #14b8a6)' }}
                  />
                  <span className="relative z-10">Start a Project</span>
                  <ArrowUpRight
                    size={15}
                    className="relative z-10 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </Link>

                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="lg:hidden w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200"
                  aria-label="Toggle menu"
                  style={{
                    background: mobileMenuOpen
                      ? isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.1)'
                      : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                    border: mobileMenuOpen
                      ? '1px solid rgba(16,185,129,0.3)'
                      : isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
                    color: mobileMenuOpen
                      ? '#10b981'
                      : isDark ? 'rgba(209,213,219,0.9)' : 'rgba(55,65,81,0.9)',
                  }}
                >
                  <div className="relative w-5 h-5">
                    <span
                      className="absolute inset-0 flex items-center justify-center transition-all duration-300"
                      style={{ opacity: mobileMenuOpen ? 0 : 1, transform: mobileMenuOpen ? 'rotate(90deg) scale(0)' : 'rotate(0) scale(1)' }}
                    >
                      <Menu size={20} />
                    </span>
                    <span
                      className="absolute inset-0 flex items-center justify-center transition-all duration-300"
                      style={{ opacity: mobileMenuOpen ? 1 : 0, transform: mobileMenuOpen ? 'rotate(0) scale(1)' : 'rotate(-90deg) scale(0)' }}
                    >
                      <X size={20} />
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      <div
        role="button"
        tabIndex={mobileMenuOpen ? 0 : -1}
        aria-label="Close navigation menu"
        className="lg:hidden fixed inset-0 z-40"
        style={{
          pointerEvents: mobileMenuOpen ? 'auto' : 'none',
          opacity: mobileMenuOpen ? 1 : 0,
          transition: 'opacity 0.35s ease',
          background: isDark ? 'rgba(0,0,0,0.65)' : 'rgba(0,0,0,0.35)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Sidebar Panel */}
      <div
        className="lg:hidden fixed top-0 right-0 bottom-0 z-50 w-[82vw] max-w-[340px] flex flex-col"
        style={{
          transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.4s cubic-bezier(0.16,1,0.3,1)',
          background: isDark
            ? 'linear-gradient(160deg, rgba(10,14,23,0.98) 0%, rgba(9,11,17,0.99) 100%)'
            : 'linear-gradient(160deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.99) 100%)',
          borderLeft: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
          boxShadow: isDark
            ? '-24px 0 80px rgba(0,0,0,0.7), -1px 0 0 rgba(255,255,255,0.04)'
            : '-24px 0 80px rgba(0,0,0,0.15), -1px 0 0 rgba(0,0,0,0.04)',
        }}
      >
        {/* Sidebar Header */}
        <div
          className="flex items-center justify-between px-6 pt-6 pb-5"
          style={{ borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)' }}
        >
          <Link to="/" className="flex items-center gap-3" onClick={() => setMobileMenuOpen(false)}>
            <img
              src="/ChatGPT_Image_Mar_14,_2026,_08_43_44_PM.png"
              alt="Matchless Digital Hub"
              className="w-9 h-9 object-contain rounded-xl"
            />
            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">Matchless Digital</p>
              <p className="text-[9px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-[0.25em] leading-tight">Hub</p>
            </div>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close navigation menu"
            className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200"
            style={{
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
              color: isDark ? 'rgba(156,163,175,0.9)' : 'rgba(107,114,128,0.9)',
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav Links */}
        <nav className="flex-1 overflow-y-auto px-4 py-5 space-y-1">
          <p
            className="px-3 mb-3 text-[10px] font-semibold uppercase tracking-[0.2em]"
            style={{ color: isDark ? 'rgba(107,114,128,0.7)' : 'rgba(156,163,175,0.9)' }}
          >
            Navigation
          </p>
          {navLinks.map((link, i) => {
            const Icon = link.icon;
            const active = isActive(link.to);
            return (
              <Link
                key={link.to}
                to={link.to}
                className="sidebar-link-enter flex items-center gap-3.5 px-4 py-3.5 rounded-2xl text-sm font-medium transition-all duration-200 group relative overflow-hidden"
                style={{
                  animationDelay: mobileMenuOpen ? `${i * 60}ms` : '0ms',
                  color: active
                    ? isDark ? '#34d399' : '#059669'
                    : isDark ? 'rgba(209,213,219,0.85)' : 'rgba(55,65,81,0.9)',
                  background: active
                    ? isDark ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.07)'
                    : 'transparent',
                }}
              >
                {active && (
                  <span
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-full"
                    style={{ background: 'linear-gradient(180deg, #10b981, #14b8a6)', boxShadow: '0 0 8px rgba(16,185,129,0.8)' }}
                  />
                )}
                <span
                  className="flex items-center justify-center w-8 h-8 rounded-xl flex-shrink-0 transition-all duration-200"
                  style={{
                    background: active
                      ? isDark ? 'rgba(16,185,129,0.2)' : 'rgba(16,185,129,0.12)'
                      : isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                    color: active
                      ? isDark ? '#34d399' : '#059669'
                      : isDark ? 'rgba(156,163,175,0.8)' : 'rgba(107,114,128,0.8)',
                  }}
                >
                  <Icon size={15} />
                </span>
                <span className="flex-1">{link.label}</span>
                {active && (
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{ background: '#10b981', boxShadow: '0 0 6px rgba(16,185,129,0.9)' }}
                  />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div
          className="px-4 pb-8 pt-4 space-y-3"
          style={{ borderTop: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)' }}
        >
          <Link
            to="/contact"
            className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl text-white text-sm font-semibold transition-all duration-200 active:scale-[0.98] relative overflow-hidden group"
            style={{
              background: 'linear-gradient(135deg, #059669, #0d9488)',
              boxShadow: '0 4px 16px rgba(16,185,129,0.35)',
            }}
            onClick={() => setMobileMenuOpen(false)}
          >
            <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'linear-gradient(135deg, #10b981, #14b8a6)' }} />
            <span className="relative z-10 flex items-center gap-2">
              Start a Project
              <ArrowUpRight size={15} />
            </span>
          </Link>

          <a
            href="mailto:hello@matchlessdigitalhub.com"
            className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl text-xs font-medium transition-all duration-200"
            style={{
              color: isDark ? 'rgba(156,163,175,0.8)' : 'rgba(107,114,128,0.8)',
              background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
              border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <ExternalLink size={12} />
            hello@matchlessdigitalhub.com
          </a>
        </div>
      </div>
    </>
  );
}
